// Geolocation utility for automatic language detection
// Maps countries to their primary languages supported by the app

const COUNTRY_TO_LANGUAGE = {
  // French-speaking countries
  'FR': 'fr', // France
  'BE': 'fr', // Belgium (also Dutch/German but French is common)
  'CH': 'fr', // Switzerland (also German/Italian but French is common)
  'CA': 'fr', // Canada (also English but French is official)
  'MC': 'fr', // Monaco
  'LU': 'fr', // Luxembourg (also German but French is common)
  'MA': 'fr', // Morocco (French is widely used)
  'TN': 'fr', // Tunisia (French is widely used)
  'DZ': 'fr', // Algeria (French is widely used)
  'SN': 'fr', // Senegal
  'CI': 'fr', // Côte d'Ivoire
  'ML': 'fr', // Mali
  'BF': 'fr', // Burkina Faso
  'NE': 'fr', // Niger
  'TD': 'fr', // Chad
  'MG': 'fr', // Madagascar
  'CM': 'fr', // Cameroon
  'CD': 'fr', // Democratic Republic of the Congo
  'CG': 'fr', // Republic of the Congo
  'GA': 'fr', // Gabon
  'CF': 'fr', // Central African Republic
  'TG': 'fr', // Togo
  'BJ': 'fr', // Benin
  'RW': 'fr', // Rwanda
  'BI': 'fr', // Burundi
  'DJ': 'fr', // Djibouti
  'KM': 'fr', // Comoros
  'VU': 'fr', // Vanuatu
  'NC': 'fr', // New Caledonia
  'PF': 'fr', // French Polynesia
  'WF': 'fr', // Wallis and Futuna
  'PM': 'fr', // Saint Pierre and Miquelon
  'RE': 'fr', // Réunion
  'YT': 'fr', // Mayotte
  'GF': 'fr', // French Guiana
  'MQ': 'fr', // Martinique
  'GP': 'fr', // Guadeloupe
  'BL': 'fr', // Saint Barthélemy
  'MF': 'fr', // Saint Martin

  // Spanish-speaking countries
  'ES': 'es', // Spain
  'MX': 'es', // Mexico
  'AR': 'es', // Argentina
  'CO': 'es', // Colombia
  'PE': 'es', // Peru
  'VE': 'es', // Venezuela
  'CL': 'es', // Chile
  'EC': 'es', // Ecuador
  'GT': 'es', // Guatemala
  'CU': 'es', // Cuba
  'BO': 'es', // Bolivia
  'DO': 'es', // Dominican Republic
  'HN': 'es', // Honduras
  'PY': 'es', // Paraguay
  'SV': 'es', // El Salvador
  'NI': 'es', // Nicaragua
  'CR': 'es', // Costa Rica
  'PA': 'es', // Panama
  'UY': 'es', // Uruguay
  'PR': 'es', // Puerto Rico
  'GQ': 'es', // Equatorial Guinea

  // German-speaking countries
  'DE': 'de', // Germany
  'AT': 'de', // Austria
  'LI': 'de', // Liechtenstein
  // Note: Switzerland (CH) is mapped to French above, but could be German too
  // We'll handle this with IP-based detection or user preference

  // English-speaking countries (default)
  'US': 'en', // United States
  'GB': 'en', // United Kingdom
  'AU': 'en', // Australia
  'NZ': 'en', // New Zealand
  'IE': 'en', // Ireland
  'ZA': 'en', // South Africa
  'SG': 'en', // Singapore
  'MY': 'en', // Malaysia
  'PH': 'en', // Philippines
  'IN': 'en', // India
  'PK': 'en', // Pakistan
  'NG': 'en', // Nigeria
  'KE': 'en', // Kenya
  'UG': 'en', // Uganda
  'TZ': 'en', // Tanzania
  'GH': 'en', // Ghana
  'ZW': 'en', // Zimbabwe
  'BW': 'en', // Botswana
  'ZM': 'en', // Zambia
  'MW': 'en', // Malawi
  'SZ': 'en', // Eswatini
  'LS': 'en', // Lesotho
  'LR': 'en', // Liberia
  'SL': 'en', // Sierra Leone
  'GM': 'en', // Gambia
  'MT': 'en', // Malta
  'CY': 'en', // Cyprus
  'FJ': 'en', // Fiji
  'PG': 'en', // Papua New Guinea
  'SB': 'en', // Solomon Islands
  'VU': 'en', // Vanuatu (also French)
  'WS': 'en', // Samoa
  'TO': 'en', // Tonga
  'TV': 'en', // Tuvalu
  'KI': 'en', // Kiribati
  'NR': 'en', // Nauru
  'PW': 'en', // Palau
  'MH': 'en', // Marshall Islands
  'FM': 'en', // Micronesia
  'GU': 'en', // Guam
  'AS': 'en', // American Samoa
  'MP': 'en', // Northern Mariana Islands
  'VI': 'en', // U.S. Virgin Islands
  'BM': 'en', // Bermuda
  'KY': 'en', // Cayman Islands
  'TC': 'en', // Turks and Caicos Islands
  'VG': 'en', // British Virgin Islands
  'AI': 'en', // Anguilla
  'MS': 'en', // Montserrat
  'SH': 'en', // Saint Helena
  'FK': 'en', // Falkland Islands
  'GI': 'en', // Gibraltar
  'JE': 'en', // Jersey
  'GG': 'en', // Guernsey
  'IM': 'en'  // Isle of Man
};

// Fallback to browser language if country detection fails
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Map browser language to supported languages
  const supportedLanguages = ['en', 'fr', 'es', 'de'];
  return supportedLanguages.includes(langCode) ? langCode : 'en';
};

// Get user's country using multiple methods
const getUserCountry = async () => {
  try {
    // Method 1: Try IP-based geolocation API (free service)
    const response = await fetch('https://ipapi.co/json/', {
      timeout: 5000
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.country_code) {
        console.log('Country detected via IP:', data.country_code);
        return data.country_code.toUpperCase();
      }
    }
  } catch (error) {
    console.log('IP geolocation failed, trying alternative method:', error.message);
  }

  try {
    // Method 2: Alternative IP service
    const response = await fetch('https://api.country.is/', {
      timeout: 5000
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.country) {
        console.log('Country detected via alternative service:', data.country);
        return data.country.toUpperCase();
      }
    }
  } catch (error) {
    console.log('Alternative geolocation failed:', error.message);
  }

  try {
    // Method 3: Try Cloudflare's service
    const response = await fetch('https://cloudflare-dns.com/dns-query?name=whoami.cloudflare&type=TXT', {
      headers: {
        'Accept': 'application/dns-json'
      },
      timeout: 5000
    });
    
    if (response.ok) {
      const data = await response.json();
      // This service returns different data, so we'll skip it for now
    }
  } catch (error) {
    console.log('Cloudflare geolocation failed:', error.message);
  }

  // Method 4: Try to detect from timezone (less accurate)
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneToCountry = {
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Madrid': 'ES',
      'Europe/Rome': 'IT',
      'America/Mexico_City': 'MX',
      'America/Toronto': 'CA',
      'Australia/Sydney': 'AU',
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Africa/Casablanca': 'MA', // Morocco
      'Africa/Tunis': 'TN', // Tunisia
      'Africa/Algiers': 'DZ', // Algeria
      'Europe/Brussels': 'BE', // Belgium
      'Europe/Zurich': 'CH', // Switzerland
      'America/Montreal': 'CA', // Canada (French)
      'Europe/Luxembourg': 'LU' // Luxembourg
    };
    
    if (timezoneToCountry[timezone]) {
      console.log('Country detected via timezone:', timezoneToCountry[timezone]);
      return timezoneToCountry[timezone];
    }
  } catch (error) {
    console.log('Timezone detection failed:', error.message);
  }

  // Fallback: return null to use browser language
  return null;
};

// Main function to detect user's preferred language
export const detectUserLanguage = async () => {
  try {
    // First, check if user has a saved language preference
    const savedLanguage = localStorage.getItem('checkout_language');
    if (savedLanguage && ['en', 'fr', 'es', 'de'].includes(savedLanguage)) {
      console.log('Using saved language preference:', savedLanguage);
      return savedLanguage;
    }

    // Try to detect country
    const country = await getUserCountry();
    
    if (country && COUNTRY_TO_LANGUAGE[country]) {
      const detectedLanguage = COUNTRY_TO_LANGUAGE[country];
      console.log(`Language detected: ${detectedLanguage} for country: ${country}`);
      
      // Save the detected language for future use
      localStorage.setItem('checkout_language', detectedLanguage);
      localStorage.setItem('checkout_detected_country', country);
      
      return detectedLanguage;
    }

    // Fallback to browser language
    const browserLanguage = getBrowserLanguage();
    console.log('Using browser language as fallback:', browserLanguage);
    
    // Save the fallback language
    localStorage.setItem('checkout_language', browserLanguage);
    
    return browserLanguage;
    
  } catch (error) {
    console.error('Error detecting user language:', error);
    return 'en'; // Ultimate fallback
  }
};

// Function to save user's language preference
export const saveLanguagePreference = (language) => {
  if (['en', 'fr', 'es', 'de'].includes(language)) {
    localStorage.setItem('checkout_language', language);
    console.log('Language preference saved:', language);
  }
};

// Function to get saved language preference
export const getSavedLanguage = () => {
  return localStorage.getItem('checkout_language') || 'en';
};

// Function to get user's country code for country selector
export const detectUserCountry = async () => {
  try {
    // First, check if user has a saved country preference
    const savedCountry = localStorage.getItem('checkout_country');
    if (savedCountry) {
      console.log('Using saved country preference:', savedCountry);
      return savedCountry;
    }

    // Try to detect country using the same methods as language detection
    const country = await getUserCountry();
    
    if (country) {
      console.log('Country detected for selector:', country);
      
      // Save the detected country for future use
      localStorage.setItem('checkout_country', country);
      
      return country;
    }

    // Fallback to US if detection fails
    console.log('Country detection failed, defaulting to US');
    return 'US';
    
  } catch (error) {
    console.error('Error detecting user country:', error);
    return 'US'; // Ultimate fallback
  }
};

// Function to save user's country preference
export const saveCountryPreference = (countryCode) => {
  if (countryCode && countryCode.length === 2) {
    localStorage.setItem('checkout_country', countryCode.toUpperCase());
    console.log('Country preference saved:', countryCode);
  }
};

// Function to get saved country preference
export const getSavedCountry = () => {
  return localStorage.getItem('checkout_country') || 'US';
};

// Function to clear language preference (for testing)
export const clearLanguagePreference = () => {
  localStorage.removeItem('checkout_language');
  localStorage.removeItem('checkout_detected_country');
  localStorage.removeItem('checkout_country');
  console.log('Language and country preferences cleared');
}; 